<template>
  <div class="flex min-h-screen bg-gray-50">
    <Sidebar
      @open-create-modal="openCreateModal"
      @logout="logout"
    />

    <!-- Main Content -->
    <div class="lg:ml-64 p-4 sm:p-6 lg:p-8 w-full">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">My Approved Requests</h1>

      <div v-if="requests.length === 0" class="text-center text-gray-500 py-8">
        <div class="bg-white rounded-lg p-8 shadow-sm">
          <svg class="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-lg font-medium text-gray-600">No approved requests yet</p>
          <p class="text-sm text-gray-500 mt-2">Requests you approve will appear here.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div v-for="request in requests" :key="request._id" class="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
          <div class="p-6">
            <!-- Header -->
            <div class="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3">
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-800 mb-1">
                  Request #{{ request._id.slice(-6).toUpperCase() }}
                </h3>
                <div class="flex flex-wrap items-center gap-2">
                  <span class="px-2 py-1 text-xs font-medium rounded-full"
                        :class="getPriorityClass(request.priority)">
                    {{ request.priority || 'Medium' }}
                  </span>
                  <span class="px-2 py-1 text-xs font-medium rounded-full"
                        :class="getStatusClass(request.status)">
                    {{ request.status }}
                  </span>
                  <span class="text-sm text-gray-500">{{ formatDate(request.createdAt) }}</span>
                </div>
              </div>
              <div class="text-left sm:text-right">
                <p class="text-2xl font-bold text-gray-900">RF {{ formatAmount(request.amount) }}</p>
                <p class="text-sm text-gray-500">{{ request.paymentMethod || 'Bank Transfer' }}</p>
              </div>
            </div>

            <!-- Request Details -->
            <div class="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div>
                <p class="text-gray-500">Requester</p>
                <p class="font-medium text-gray-900">{{ request.requester?.name || 'Unknown User' }}</p>
              </div>
              <div>
                <p class="text-gray-500">Department</p>
                <p class="font-medium text-gray-900">{{ request.department || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-500">Beneficiary</p>
                <p class="font-medium text-gray-900">{{ request.supplier || 'N/A' }}</p>
              </div>
              <div>
                <p class="text-gray-500">Payment Date</p>
                <p class="font-medium text-gray-900">{{ formatDate(request.preferredPaymentDate) }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div class="col-span-2">
                <p class="text-gray-500">Beneficiary Account/MoMo</p>
                <p class="font-medium text-gray-900">{{ request.beneficiaryAccount || 'N/A' }}</p>
              </div>
            </div>

            <div class="mb-4">
              <p class="text-gray-500 text-sm mb-1">Purpose</p>
              <p class="text-gray-900 text-sm bg-gray-50 p-2 rounded">{{ request.purpose }}</p>
            </div>

            <!-- Approval Path Visualization -->
            <ApprovalPathVisualization
              :amount="request.amount"
              :approvalPath="request.approvalPath"
              :status="request.status"
              class="mb-4"
            />

            <!-- Comments Section -->
            <div v-if="hasComments(request)" class="mb-4">
              <p class="text-gray-500 text-sm mb-2">Review Comments</p>
              <div class="space-y-2">
                <!-- Finance Review Comments -->
                <div v-if="request.financeReview && request.financeReview.comments"
                     class="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
                  <div class="flex items-center mb-1">
                    <div class="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span class="text-xs font-medium text-blue-700">Finance Review</span>
                    <span class="text-xs text-blue-600 ml-auto">{{ formatDate(request.financeReview.reviewedAt) }}</span>
                  </div>
                  <p class="text-sm text-blue-800">{{ request.financeReview.comments }}</p>
                  <div v-if="request.financeReview.reviewedBy" class="text-xs text-blue-600 mt-1">
                    By: {{ request.financeReview.reviewedBy.name }}
                  </div>
                </div>

                <!-- MD Review Comments -->
                <div v-if="request.mdReview && request.mdReview.comments"
                     class="bg-purple-50 border-l-4 border-purple-400 p-3 rounded-r">
                  <div class="flex items-center mb-1">
                    <div class="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <span class="text-xs font-medium text-purple-700">Managing Director Review</span>
                    <span class="text-xs text-purple-600 ml-auto">{{ formatDate(request.mdReview.reviewedAt) }}</span>
                  </div>
                  <p class="text-sm text-purple-800">{{ request.mdReview.comments }}</p>
                  <div v-if="request.mdReview.reviewedBy" class="text-xs text-purple-600 mt-1">
                    By: {{ request.mdReview.reviewedBy.name }}
                  </div>
                  <div v-if="request.mdReview.approvedAmount !== request.amount" class="text-xs text-purple-600 mt-1">
                    Approved Amount: RF {{ formatAmount(request.mdReview.approvedAmount) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Attachments -->
            <div v-if="hasAttachments(request)" class="mb-4">
              <p class="text-gray-500 text-sm mb-2">Attachments</p>
              <div class="flex flex-wrap gap-2">
                <button v-if="request.attachments?.invoice"
                        @click="downloadAttachment(request._id, 'invoice')"
                        class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors">
                  📄 Invoice
                </button>
                <button v-if="request.attachments?.contract"
                        @click="downloadAttachment(request._id, 'contract')"
                        class="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 transition-colors">
                  📋 Contract
                </button>
                <button v-for="(doc, index) in request.attachments?.otherDocuments || []"
                        :key="index"
                        @click="downloadAttachment(request._id, `other-${index}`)"
                        class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
                  📎 Document {{ index + 1 }}
                </button>
              </div>
            </div>

            <!-- Settlement Status -->
            <div v-if="request.status === 'approved'" class="mb-4">
              <div class="bg-green-50 border-l-4 border-green-400 p-3 rounded-r">
                <div class="flex items-center">
                  <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span class="text-xs font-medium text-green-700">
                    Settlement Status: {{ getSettlementStatusText(request.settlementStatus) }}
                  </span>
                </div>
                <div v-if="request.settlementStatus === 'paid' && request.paymentReference" class="text-xs text-green-600 mt-1">
                  Payment Reference: {{ request.paymentReference }}
                </div>
                <div v-if="request.paymentDate" class="text-xs text-green-600 mt-1">
                  Payment Date: {{ formatDate(request.paymentDate) }}
                </div>
              </div>
            </div>

            <!-- View Details Button -->
            <div>
              <button @click="viewRequestDetails(request)"
                      class="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 transition-all duration-200 font-medium">
                View Full Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Request Modal -->
      <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white bg-opacity-90 backdrop-blur-lg rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CreateRequest @request-created="fetchRequestsAndCloseModal" @close="closeCreateModal" />
        </div>
      </div>

      <!-- Request Details Modal -->
      <div v-if="showDetailsModal && selectedRequest" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Request Details</h3>
            <button @click="closeDetailsModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="space-y-4">
            <div class="bg-gray-50 p-4 rounded-lg">
              <h4 class="font-semibold mb-2">Request #{{ selectedRequest._id.slice(-6).toUpperCase() }}</h4>
              <div class="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span class="text-gray-500">Amount:</span>
                  <span class="font-medium ml-2">RF {{ formatAmount(selectedRequest.amount) }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Status:</span>
                  <span class="font-medium ml-2 capitalize">{{ selectedRequest.status }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Priority:</span>
                  <span class="font-medium ml-2">{{ selectedRequest.priority }}</span>
                </div>
                <div>
                  <span class="text-gray-500">Payment Method:</span>
                  <span class="font-medium ml-2">{{ selectedRequest.paymentMethod }}</span>
                </div>
                <div class="col-span-2">
                  <span class="text-gray-500">Requester:</span>
                  <span class="font-medium ml-2">{{ selectedRequest.requester?.name }}</span>
                </div>
                <div class="col-span-2">
                  <span class="text-gray-500">Department:</span>
                  <span class="font-medium ml-2">{{ selectedRequest.department }}</span>
                </div>
                <div class="col-span-2">
                  <span class="text-gray-500">Purpose:</span>
                  <p class="mt-1 bg-white p-2 rounded">{{ selectedRequest.purpose }}</p>
                </div>
              </div>
            </div>

            <ApprovalPathVisualization
              :amount="selectedRequest.amount"
              :approvalPath="selectedRequest.approvalPath"
              :status="selectedRequest.status"
            />
          </div>

          <div class="mt-6 flex justify-end">
            <button @click="closeDetailsModal"
                    class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CreateRequest from '../components/CreateRequest.vue';
import Sidebar from '../components/Sidebar.vue';
import ApprovalPathVisualization from '../components/ApprovalPathVisualization.vue';

export default {
  components: { CreateRequest, Sidebar, ApprovalPathVisualization },
  data() {
    return {
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      requests: [],
      showCreateModal: false,
      showDetailsModal: false,
      selectedRequest: null
    };
  },
  async mounted() {
    await this.fetchRequests();
  },
  methods: {
    async fetchRequests() {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/my-approvals', { headers });
        this.requests = res.data;
      } catch (error) {
        console.error('Error fetching approved requests:', error);
      }
    },

    viewRequestDetails(request) {
      this.selectedRequest = request;
      this.showDetailsModal = true;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedRequest = null;
    },

    async downloadAttachment(requestId, type) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/attachment/${requestId}/${type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}-${requestId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment');
      }
    },

    hasAttachments(request) {
      return request.attachments && (
        request.attachments.invoice ||
        request.attachments.contract ||
        (request.attachments.otherDocuments && request.attachments.otherDocuments.length > 0)
      );
    },

    hasComments(request) {
      return (
        (request.financeReview && request.financeReview.comments) ||
        (request.mdReview && request.mdReview.comments)
      );
    },

    getPriorityClass(priority) {
      switch (priority?.toLowerCase()) {
        case 'high':
          return 'bg-red-100 text-red-800';
        case 'medium':
          return 'bg-yellow-100 text-yellow-800';
        case 'low':
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getStatusClass(status) {
      switch (status?.toLowerCase()) {
        case 'approved':
          return 'bg-green-100 text-green-800';
        case 'rejected':
          return 'bg-red-100 text-red-800';
        case 'reversed':
          return 'bg-amber-100 text-amber-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    },

    getSettlementStatusText(status) {
      switch (status) {
        case 'pending_payment':
          return 'Pending Payment';
        case 'paid':
          return 'Paid';
        case 'cancelled':
          return 'Cancelled';
        default:
          return status || 'N/A';
      }
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-RW').format(amount || 0);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString('en-RW', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    openCreateModal() {
      this.showCreateModal = true;
    },

    closeCreateModal() {
      this.showCreateModal = false;
    },

    fetchRequestsAndCloseModal() {
      this.fetchRequests();
      this.closeCreateModal();
    },

    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>
